/**
 * 처방전 하이라이터 — 레거시 PrescriptionHighlighterModal 포팅.
 * 좌표 계산은 레거시 Next API 대신 클라이언트 analysisPipeline을 사용한다.
 */
import { ocrService } from '@/entities/ocr/api/ocr.api';
import { prescriptionService } from '@/entities/prescription/api/prescription.api';
import {
  HighlightCanvas,
  type HighlighterTool,
  type ManualBand,
  type SelectedBand,
} from '@/pages/prescriptions/HighlightCanvas';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Checkbox } from '@/shared/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import {
  computeHighlightGeometry,
  toGeometryDrugs,
  type HighlightGeometryRow,
} from '@/shared/lib/highlighter/computeHighlightGeometry';
import {
  exportAllCompanyHighlights,
  exportCompanyHighlight,
  exportCurrentView,
} from '@/shared/lib/highlighter/exportHighlightedImages';
import { createCompanyColorMap } from '@/shared/lib/highlighter/highlightDrawing';
import {
  applyBandEdits,
  clampVertices,
  nextManualBandId,
} from '@/shared/lib/highlighter/manualBands';
import { useToast } from '@/shared/utils/toast';
import { Download, RefreshCw, Save, Trash2 } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

const MANUAL_MIN_THICKNESS = 0.008;
const MANUAL_MAX_THICKNESS = 0.14;

export interface HighlighterDrugInput {
  drugCode?: string | null;
  drugName?: string | null;
  pharmaCompanyName?: string | null;
  price?: number | null;
  outHospitalQuantity?: number | null;
  inHospitalQuantity?: number | null;
  totalAmount?: number | null;
}

export interface PrescriptionHighlighterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  imageUrl?: string | null;
  imageName?: string | null;
  drugs: HighlighterDrugInput[];
  yearMonth?: string | null;
  prescriptionImageCode: string;
}

const groupByCompany = (rows: HighlightGeometryRow[]) => {
  const map = new Map<
    string,
    { company: string; rowIds: string[]; rows: HighlightGeometryRow[] }
  >();
  rows.forEach((row) => {
    const company = row.rawPharmaCompanyName || '미분류 제약사';
    const current = map.get(company) || { company, rowIds: [], rows: [] };
    current.rowIds.push(row.rowId);
    current.rows.push(row);
    map.set(company, current);
  });
  return [...map.values()].sort((left, right) =>
    left.company.localeCompare(right.company, 'ko'),
  );
};

export const PrescriptionHighlighterDialog: React.FC<
  PrescriptionHighlighterDialogProps
> = ({
  open,
  onOpenChange,
  imageUrl,
  imageName,
  drugs,
  yearMonth,
  prescriptionImageCode,
}) => {
  const toast = useToast();
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>(
    'idle',
  );
  const [errorMsg, setErrorMsg] = useState('');
  const [source, setSource] = useState<'raw' | 'reocr' | null>(null);
  const [rows, setRows] = useState<HighlightGeometryRow[]>([]);
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [manualBands, setManualBands] = useState<ManualBand[]>([]);
  const [bandEdits, setBandEdits] = useState<
    Record<string, { dx?: number; dy?: number; deleted?: boolean }>
  >({});
  const [tool, setTool] = useState<HighlighterTool>('select');
  const [thickness, setThickness] = useState(0.022);
  const [selectedBand, setSelectedBand] = useState<SelectedBand | null>(null);

  const companies = useMemo(() => groupByCompany(rows), [rows]);
  const companyColors = useMemo(
    () => createCompanyColorMap(companies.map((item) => item.company)),
    [companies],
  );
  const editedRows = useMemo(
    () => applyBandEdits(rows, bandEdits) as HighlightGeometryRow[],
    [rows, bandEdits],
  );
  const selectedRowIdSet = useMemo(
    () => new Set(selectedRowIds),
    [selectedRowIds],
  );
  const imageAsset = useMemo(
    () =>
      imageUrl
        ? { previewUrl: imageUrl, name: imageName || prescriptionImageCode }
        : null,
    [imageUrl, imageName, prescriptionImageCode],
  );
  const matchedCount = useMemo(
    () => rows.filter((row) => !row.unmatched).length,
    [rows],
  );

  const resetState = useCallback(() => {
    setStatus('idle');
    setErrorMsg('');
    setSource(null);
    setRows([]);
    setSelectedRowIds([]);
    setManualBands([]);
    setBandEdits({});
    setTool('select');
    setSelectedBand(null);
  }, []);

  const loadGeometry = useCallback(async () => {
    if (!imageUrl || !(drugs && drugs.length)) {
      setStatus('error');
      setErrorMsg('처방 이미지 또는 확정 약품 목록이 없습니다.');
      return;
    }
    setStatus('loading');
    setErrorMsg('');
    setSelectedBand(null);

    try {
      const geometryDrugs = toGeometryDrugs(drugs);
      let usedSource: 'raw' | 'reocr' = 'reocr';
      let ocrRawUrl: string | null = null;
      let documentPayload: unknown = null;

      const raw = (await prescriptionService
        .getPrescriptionOcrRaw(prescriptionImageCode)
        .catch(() => null)) as { url?: string } | null;

      if (raw?.url) {
        ocrRawUrl = raw.url;
        usedSource = 'raw';
        try {
          // CORS 실패 대비: 미리 document도 준비하지 않고 raw만 시도
          const data = await computeHighlightGeometry({
            ocrRawUrl,
            drugs: geometryDrugs,
            yearMonth,
          });
          const nextRows = data.rows || [];
          setRows(nextRows);
          setSource(usedSource);
          setSelectedRowIds(
            nextRows.filter((row) => !row.unmatched).map((row) => row.rowId),
          );
          setManualBands([]);
          setBandEdits({});
          setStatus('done');
          return;
        } catch {
          // S3 CORS 등 → 재OCR 폴백
          usedSource = 'reocr';
        }
      }

      const reocr = await ocrService.ocrPrescriptionFromUrlRaw({ imageUrl });
      documentPayload = (reocr as { document?: unknown })?.document ?? reocr;
      const data = await computeHighlightGeometry({
        document: documentPayload,
        drugs: geometryDrugs,
        yearMonth,
      });
      const nextRows = data.rows || [];
      setRows(nextRows);
      setSource(usedSource);
      setSelectedRowIds(
        nextRows.filter((row) => !row.unmatched).map((row) => row.rowId),
      );
      setManualBands([]);
      setBandEdits({});
      setStatus('done');
    } catch (error) {
      console.error('[highlighter geometry]', error);
      setStatus('error');
      setErrorMsg(
        error instanceof Error
          ? error.message
          : '좌표 계산에 실패했습니다.',
      );
    }
  }, [imageUrl, drugs, prescriptionImageCode, yearMonth]);

  useEffect(() => {
    if (open) {
      void loadGeometry();
      return;
    }
    resetState();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps -- 열릴 때만 로드

  const toggleRow = (rowId: string) => {
    setSelectedRowIds((prev) =>
      prev.includes(rowId)
        ? prev.filter((id) => id !== rowId)
        : [...prev, rowId],
    );
  };

  const setCompanySelected = (company: string, checked: boolean) => {
    const ids =
      companies.find((item) => item.company === company)?.rowIds || [];
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (checked ? next.add(id) : next.delete(id)));
      return [...next];
    });
  };

  const selectAll = (checked: boolean) => {
    setSelectedRowIds(checked ? rows.map((row) => row.rowId) : []);
  };

  const addManualBand = (vertices: Array<{ x: number; y: number }>) => {
    const id = nextManualBandId();
    setManualBands((prev) => [
      ...prev,
      { id, vertices: clampVertices(vertices), color: '#fa541c' },
    ]);
    setTool('edit');
    setSelectedBand({ type: 'manual', id });
  };

  const moveBand = (band: SelectedBand, dx: number, dy: number) => {
    if (band.type === 'manual') {
      setManualBands((prev) =>
        prev.map((item) =>
          item.id === band.id
            ? {
                ...item,
                vertices: item.vertices.map((vertex) => ({
                  x: vertex.x + dx,
                  y: vertex.y + dy,
                })),
              }
            : item,
        ),
      );
      return;
    }
    setBandEdits((prev) => {
      const current = prev[band.id] || {};
      return {
        ...prev,
        [band.id]: {
          ...current,
          dx: (current.dx || 0) + dx,
          dy: (current.dy || 0) + dy,
        },
      };
    });
  };

  const deleteSelectedBand = () => {
    if (!selectedBand) return;
    if (selectedBand.type === 'manual') {
      setManualBands((prev) =>
        prev.filter((item) => item.id !== selectedBand.id),
      );
    } else {
      setBandEdits((prev) => ({
        ...prev,
        [selectedBand.id]: { ...prev[selectedBand.id], deleted: true },
      }));
    }
    setSelectedBand(null);
  };

  const handleExportCompany = async (company: string) => {
    if (!imageUrl) return;
    try {
      await exportCompanyHighlight({
        imageSrc: imageUrl,
        imageName: imageName || prescriptionImageCode,
        yyyymm: yearMonth,
        hospitalName: null,
        rows: editedRows,
        selectedRowIds,
        companyName: company,
        companyColors,
      });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : '저장에 실패했습니다.',
      );
    }
  };

  const handleExportAll = async () => {
    if (!imageUrl) return;
    try {
      const count = await exportAllCompanyHighlights({
        imageSrc: imageUrl,
        imageName: imageName || prescriptionImageCode,
        yyyymm: yearMonth,
        hospitalName: null,
        rows: editedRows,
        selectedRowIds,
        companyColors,
      });
      toast.success(`${count}개 제약사 하이라이트를 저장했습니다.`);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : '저장에 실패했습니다.',
      );
    }
  };

  const handleSaveAs = async () => {
    if (!imageUrl) return;
    const fallback = `${yearMonth || 'NA'}_${imageName || 'highlight'}`;
    const fileName = window.prompt('저장할 파일명을 입력하세요.', fallback);
    if (!fileName) return;
    try {
      await exportCurrentView({
        imageSrc: imageUrl,
        rows: editedRows,
        selectedRowIds,
        manualBands,
        companyColors,
        fileName,
      });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : '저장에 실패했습니다.',
      );
    }
  };

  const allSelected = rows.length > 0 && selectedRowIds.length === rows.length;
  const someSelected = selectedRowIds.length > 0 && !allSelected;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[100dvh] max-h-[100dvh] w-screen max-w-none flex-col gap-0 rounded-none p-0 sm:max-w-none">
        <DialogHeader className="shrink-0 border-b px-4 py-3">
          <DialogTitle>처방전 하이라이터</DialogTitle>
        </DialogHeader>

        <div className="flex shrink-0 flex-wrap items-center gap-3 border-b px-4 py-2">
          <div className="inline-flex rounded-md border p-0.5">
            {(
              [
                ['select', '선택'],
                ['edit', '편집(이동)'],
                ['draw', '그리기'],
              ] as const
            ).map(([value, label]) => (
              <Button
                key={value}
                size="sm"
                variant={tool === value ? 'default' : 'ghost'}
                disabled={status !== 'done'}
                onClick={() => {
                  setTool(value);
                  setSelectedBand(null);
                }}
              >
                {label}
              </Button>
            ))}
          </div>
          {tool === 'draw' ? (
            <div className="flex min-w-48 items-center gap-2">
              <Label className="text-xs text-muted-foreground">두께</Label>
              <input
                type="range"
                className="w-36"
                min={MANUAL_MIN_THICKNESS}
                max={MANUAL_MAX_THICKNESS}
                step={0.002}
                value={thickness}
                onChange={(e) => setThickness(Number(e.target.value))}
              />
              <span className="text-xs text-muted-foreground">
                {thickness.toFixed(3)}
              </span>
            </div>
          ) : null}
          <Button
            size="sm"
            variant="destructive"
            disabled={!selectedBand}
            onClick={deleteSelectedBand}
          >
            <Trash2 className="mr-1 h-3.5 w-3.5" />
            선택 삭제
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={status !== 'done'}
            onClick={() => void handleSaveAs()}
          >
            <Save className="mr-1 h-3.5 w-3.5" />
            다른 이름으로 저장
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={status === 'loading'}
            onClick={() => void loadGeometry()}
          >
            <RefreshCw className="mr-1 h-3.5 w-3.5" />
            자동 좌표 다시 불러오기
          </Button>
          {status === 'done' ? (
            <p className="text-xs text-muted-foreground">
              {tool === 'draw'
                ? '드래그로 밴드를 그립니다(방향=각도, 길이=크기).'
                : tool === 'edit'
                  ? '밴드를 클릭해 선택 후 드래그로 이동, Delete로 삭제.'
                  : '행/제약사를 선택하면 자동 하이라이트가 표시됩니다.'}
            </p>
          ) : null}
        </div>

        <div className="min-h-0 flex-1 overflow-hidden p-3">
          {status === 'loading' ? (
            <div className="flex h-full items-center justify-center">
              <LoadingSpinner tip="처방전 좌표를 불러오는 중..." />
            </div>
          ) : null}

          {status === 'error' ? (
            <div className="space-y-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4">
              <p className="font-medium text-destructive">
                하이라이트 좌표를 불러오지 못했습니다.
              </p>
              <p className="text-sm text-muted-foreground">{errorMsg}</p>
              <Button size="sm" onClick={() => void loadGeometry()}>
                <RefreshCw className="mr-1 h-3.5 w-3.5" />
                다시 시도
              </Button>
            </div>
          ) : null}

          {/* 모바일은 이미지 위·제약사 목록 아래로 쌓는다 */}
          {status === 'done' || status === 'idle' ? (
            <div className="flex h-full min-h-0 flex-col gap-4 lg:flex-row">
              <div className="min-h-0 min-w-0 flex-1 overflow-auto rounded-lg bg-muted/40 p-3">
                {source === 'reocr' ? (
                  <div className="mb-2 rounded-md border bg-background px-3 py-2 text-sm text-muted-foreground">
                    저장된 OCR 좌표가 없어 즉석 재OCR로 좌표를 계산했습니다.
                  </div>
                ) : null}
                {status === 'done' && matchedCount === 0 ? (
                  <div className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                    자동 위치 검출에 실패했습니다. 그리기 도구로 직접
                    하이라이트를 추가하세요.
                  </div>
                ) : null}
                <HighlightCanvas
                  imageAsset={imageAsset}
                  rows={editedRows}
                  selectedRowIds={selectedRowIds}
                  companyColors={companyColors}
                  onToggleRow={toggleRow}
                  tool={tool}
                  manualBands={manualBands}
                  thickness={thickness}
                  selectedBand={selectedBand}
                  onSelectBand={setSelectedBand}
                  onMoveBand={moveBand}
                  onDrawBand={addManualBand}
                  onDeleteSelected={deleteSelectedBand}
                />
              </div>

              <div className="max-h-[40%] w-full shrink-0 overflow-auto pr-1 lg:max-h-none lg:w-[380px]">
                {!companies.length ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">
                    표시할 제약사가 없습니다.
                  </p>
                ) : (
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <label className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={
                            allSelected
                              ? true
                              : someSelected
                                ? 'indeterminate'
                                : false
                          }
                          onCheckedChange={(checked) =>
                            selectAll(checked === true)
                          }
                        />
                        전체 선택
                      </label>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!selectedRowIds.length}
                        onClick={() => void handleExportAll()}
                      >
                        <Download className="mr-1 h-3.5 w-3.5" />
                        선택 전체 저장
                      </Button>
                    </div>

                    {companies.map((group) => {
                      const selectedInGroup = group.rowIds.filter((id) =>
                        selectedRowIdSet.has(id),
                      ).length;
                      const allInGroup =
                        selectedInGroup === group.rowIds.length;
                      return (
                        <div
                          key={group.company}
                          className="rounded-lg border p-3"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <Badge
                                style={{
                                  backgroundColor:
                                    companyColors[group.company] || '#1677ff',
                                  color: '#fff',
                                }}
                              >
                                {group.company}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {group.rowIds.length}개
                              </span>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={!selectedInGroup}
                              onClick={() =>
                                void handleExportCompany(group.company)
                              }
                            >
                              <Download className="mr-1 h-3.5 w-3.5" />
                              저장
                            </Button>
                          </div>
                          <label className="mt-2 flex items-center gap-2 text-sm">
                            <Checkbox
                              checked={
                                allInGroup
                                  ? true
                                  : selectedInGroup > 0
                                    ? 'indeterminate'
                                    : false
                              }
                              onCheckedChange={(checked) =>
                                setCompanySelected(
                                  group.company,
                                  checked === true,
                                )
                              }
                            />
                            이 제약사 전체
                          </label>
                          <div className="mt-2 space-y-1 border-t pt-2">
                            {group.rows.map((row) => (
                              <label
                                key={row.rowId}
                                className="flex items-start gap-2 text-sm"
                              >
                                <Checkbox
                                  className="mt-0.5"
                                  checked={selectedRowIdSet.has(row.rowId)}
                                  onCheckedChange={() => toggleRow(row.rowId)}
                                />
                                <span
                                  className={
                                    row.unmatched
                                      ? 'text-muted-foreground line-through'
                                      : undefined
                                  }
                                >
                                  {row.rawDrugName ||
                                    row.rawDrugCode ||
                                    row.rowId}
                                  {row.unmatched ? (
                                    <span className="ml-1 text-xs text-amber-600 no-underline">
                                      위치 미검출
                                    </span>
                                  ) : null}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>

        <DialogFooter className="shrink-0 border-t px-4 py-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            닫기
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export type { HighlightGeometryRow };
